<template>
  <div :class="b('', { mix: className, isMobileView, isEditMode })">
    <div :class="b('navigation', {
      left: isLeft, right: isRight, 'not-visible': tabs.length < 2, isMobileView })">
      <div
        v-for="(tab, index) in activeTabsMobile"
        :key="tab.id"
        :class="b('tab-wrap', { 'two-block': tabs.length === 2, isMobileView, show: tab.isActive })"
        :data-tab-id="tab.id"
      >
        <div
          v-if="isMobileView"
          :class="b('tab-content-wrap', { show: tab.isActive, hide: !tab.isActive, isMobileView })"
        >
          <slot :name="`tab-${tab.id}`" :tab="tab">
            {{ tab.content }}
          </slot>
        </div>
        <button
          :class="b('tab', {
            mix: classNameTab,
            active: isMobileView ? tab.isActive : activeTab === tab.id,
            isMobileView,
            editable: isEditMode,
          })"
          type="button"
          @click="handleTabClick(tab.id)"
        >
          <span :class="b('tab-text', { isMobileView })">
            {{ isMobileView ? (tab.nameMob || tab.name) : tab.name }}
          </span>
          <PencilIcon
            v-if="isEditMode"
            :class="b('tab-edit-icon')"
            :color="isMobileView ? '#fff' : '#5f8aa4'"
            :size="14"
            @click.stop="handleTabPencilClick(index, $event)"
          />
        </button>
      </div>
    </div>
    <div
      v-if="!isMobileView"
      :class="b('content', { 'not-tab': tabs.length < 2, isMobileView })"
    >
      <slot :name="`content-${activeTab}`" :tab="activeTabData">
        {{ activeTabContent }}
      </slot>
    </div>
    <CustomButton
      v-if="activeTabData?.withButton"
      :button-func="activeTabData.onClick"
      :class="b('button', { isMobileView })"
    >
      {{ activeTabData.buttonText }}
    </CustomButton>
    <div
      v-if="isMobileView"
      :class="b('mobile-line', { isMobileView })"
    />

    <!-- Tab Title Modal -->
    <EditTitleModal
      :visible="tabTitleModalVisible"
      :title="editingTabName"
      :title-mobile="editingTabNameMob"
      modal-title="Edit Tab Title"
      @close="closeTabTitleModal"
      @save="handleTabTitleSave"
      @delete="handleTabTitleDelete"
    />
  </div>
</template>

<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import CustomButton from './custom-button.vue';
import styles from './trust-tabs-style.scss';

export default {
  name: 'TrustTabs',
  components: {
    PencilIcon,
    EditTitleModal,
    CustomButton,
  },
  props: {
    className: {
      type: String,
      default: '',
    },
    classNameTab: {
      type: String,
      default: '',
    },
    tabs: {
      type: Array,
      default: () => [
        { id: 'secure', name: 'SECURE SHIPPING' },
        { id: 'process', name: 'HANDS-ON PROCESS' },
        { id: 'track', name: 'TRACK ANYTIME' },
        { id: 'support', name: 'ONGOING SUPPORT' },
      ],
    },
    isLeft: {
      type: Boolean,
      default: false,
    },
    isRight: {
      type: Boolean,
      default: false,
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['tab-change', 'update-tab'],
  data() {
    return {
      activeTab: this.tabs[0]?.id || '',
      activeTabsMobile: [],
      // Tab title modal
      tabTitleModalVisible: false,
      editingTabIndex: null,
    };
  },
  computed: {
    activeTabContent() {
      return this.tabs.find((tab) => tab.id === this.activeTab)?.content;
    },
    activeTabData() {
      return this.tabs.find((tab) => tab.id === this.activeTab);
    },
    editingTab() {
      if (this.editingTabIndex === null) return null;
      return this.tabs[this.editingTabIndex] || null;
    },
    editingTabName() {
      return this.editingTab?.name || '';
    },
    editingTabNameMob() {
      return this.editingTab?.nameMob || this.editingTab?.name || '';
    },
  },
  watch: {
    tabs: {
      handler() {
        this.initializeTabs();
      },
      immediate: true,
    },
  },
  methods: {
    b: bem('trust-tabs', styles),
    initializeTabs() {
      // Для десктопа - сохраняем текущий выбранный таб или выбираем первый
      const currentExists = this.activeTab && this.tabs.some((t) => t.id === this.activeTab);
      if (!currentExists) {
        this.activeTab = this.tabs[0]?.id || '';
      }
      // Для мобилки - все табы закрыты по умолчанию
      this.activeTabsMobile = this.tabs.map((tab) => ({
        ...tab,
        isActive: false,
      }));
    },
    handleTabClick(tabId) {
      if (this.isMobileView) {
        this.handleTabClickMobile(tabId);
      } else {
        this.handleTabClickDesktop(tabId);
      }
    },
    handleTabClickDesktop(tabId) {
      this.activeTab = tabId;
      this.$emit('tab-change', tabId);
    },
    handleTabClickMobile(tabId) {
      const currentTab = this.activeTabsMobile.find((tab) => tab.id === tabId);
      const isOpening = !currentTab?.isActive;

      this.activeTabsMobile = this.activeTabsMobile.map((tab) => ({
        ...tab,
        isActive: tab.id === tabId ? !tab.isActive : false,
      }));

      if (isOpening) {
        this.$nextTick(() => {
          const element = document.querySelector(`[data-tab-id="${tabId}"]`);
          if (element) {
            const rect = element.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            window.scrollTo({
              top: scrollTop + rect.top - 100,
              behavior: 'smooth',
            });
          }
        });
      }

      this.$emit('tab-change', tabId);
    },
    // Tab title editing
    handleTabPencilClick(index, event) {
      event.stopPropagation();
      if (this.isEditMode) {
        this.editingTabIndex = index;
        this.tabTitleModalVisible = true;
      }
    },
    closeTabTitleModal() {
      this.tabTitleModalVisible = false;
      this.editingTabIndex = null;
    },
    handleTabTitleSave(values) {
      if (this.editingTabIndex === null) return;
      this.$emit('update-tab', {
        index: this.editingTabIndex,
        updates: {
          name: values.title,
          nameMob: values.titleMobile,
        },
      });
      this.closeTabTitleModal();
    },
    handleTabTitleDelete() {
      if (this.editingTabIndex === null) return;
      this.$emit('update-tab', {
        index: this.editingTabIndex,
        updates: {
          name: '',
          nameMob: '',
        },
      });
      this.closeTabTitleModal();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './trust-tabs-style.scss';
</style>
